/**
 * 63.15 step 1 (DECISIONS 203; the owner: "better jump (currently doesn't work …)"; on the note: "climbing later"): with
 * GameConfig.jumpFeel a person's jump has its own feel. It leaves the ground at √(2 g_j h_j) for its apex (`jumpApex`, JUMP's
 * 0.6 people) under its own gravity (`jumpGravity`, JUMP's 10 people a second²) — about 0.69 s in the air, where the felt
 * gravity's hop was ~0.12 s, a blink — and keeps that gravity while it is above its take-off height (`PlayerState.jumpFrom`);
 * below it the felt gravity returns, so a jump off a ledge falls as anything falls. Landing on things is step 2 (later). Off:
 * nothing here runs and no field is written. Pure.
 */
import type { GameConfig, PlanetState, PlayerState, Vec3 } from '../types';
import { JUMP, PERSON_BODY_HEIGHT } from '../constants';
import { vecLength, vecSub } from '../math';
import { gravityAccel } from './physics';
import { personSetOf } from './scaleProfile';
import { defaultOn } from './defaultOn';

/** The jump's own gravity in world units a second² (its dial, in people). */
function jumpG(config: GameConfig): number {
  return (config.jumpGravity ?? JUMP.gravityPeople) * PERSON_BODY_HEIGHT * personSetOf({ config }).k;
}

/** A jump's take-off speed (world units a second, up) with the feel on: to its apex under its own gravity. */
export function jumpSpeed(config: GameConfig): number {
  return Math.sqrt(2 * jumpG(config) * (config.jumpApex ?? JUMP.apexPeople) * PERSON_BODY_HEIGHT * personSetOf({ config }).k);
}

/**
 * The gravity scale for a jumper's airborne step now: its own gravity's share of the planets' pull where it is (the step
 * scales the N-body pull), while it is above its take-off height; null (the felt gravity, as any airborne) otherwise.
 */
export function jumpGravityScale(config: GameConfig, p: Pick<PlayerState, 'jumpFrom' | 'airbornePos' | 'planetIndex' | 'airborneCause'>, planets: PlanetState[]): number | null {
  if (!defaultOn(config, 'jumpFeel') || p.jumpFrom === undefined || !p.airbornePos || p.airborneCause !== 'jump') return null;
  const planet = planets[p.planetIndex];
  if (!planet || vecLength(vecSub(p.airbornePos, planet.position)) < p.jumpFrom) return null;
  const pull = vecLength(gravityAccel(p.airbornePos, planets as never));
  return pull > 0 ? jumpG(config) / pull : null;
}

/** Its take-off height: the distance from its planet's centre where it left the ground. */
export function jumpFromOf(planet: PlanetState, pos: Vec3): number {
  return vecLength(vecSub(pos, planet.position));
}
